import * as React from 'react';
import { useState, useEffect } from 'react';
import { Modal, Row, Col } from 'antd';
import { useIntl } from 'react-intl';
import { AiFillEnvironment } from 'react-icons/ai';
import { Form, Formik } from 'formik';
import LanguagesTab from 'src/shared/components/languages-tab';
import LanguageCode from 'src/shared/services/types/language-code';
import ValueModalLanguage from './value-modal-language';
import FormItem from 'src/shared/components/form-item';
import { Input, Switch } from 'formik-antd';
import { validationInputNumber } from 'src/shared/functions/validation-input-number';
import IntlMessages from 'src/domain/utility/IntlMessages';
import { countriesService } from 'src/shared/services/configuration/countries';
import { DistrictsListDto } from 'src/shared/services/configuration/countries/dtos/districts-list-dto';
import { DistrictCreatePopupDto } from 'src/shared/services/configuration/countries/dtos/district-create-popup-dto';
import MainUtils from 'src/shared/utils/main';
import { useLocaleContext } from 'src/domain/utility/AppContextProvider/LocaleContextProvide';
import { getLocaleValue } from 'src/shared/functions/change-locales';
import { CreateOrUpdateDistrictSchema } from './schema';

interface CreateOrUpdateDistrictModalProps {
  visible: boolean;
  regionId: string;
  districtDetails?: DistrictsListDto;
  onFinish: () => void;
  onCancel: () => void;
}

const CreateOrUpdateDistrictModal: React.FC<
  CreateOrUpdateDistrictModalProps
> = (props) => {
  const { visible, regionId, districtDetails, onFinish, onCancel } = props;
  const { messages } = useIntl();
  const { locale }: any = useLocaleContext();

  const [initialValuesForm, setInitialValuesForm] =
    useState<DistrictCreatePopupDto>({
      name: '',
      code: '',
      shippingCost: 0,
      published: false,
      displayOrder: 0,
      regionId: '',
      locales: [],
    });

  useEffect(() => {
    if (districtDetails) {
      setInitialValuesForm({
        id: districtDetails?.Id,
        name: districtDetails?.Name,
        code: districtDetails?.Code,
        shippingCost: districtDetails?.ShippingCost,
        published: districtDetails?.Published,
        displayOrder: districtDetails?.DisplayOrder,
        regionId: regionId,
        locales:
          districtDetails?.Locales?.map((item) => {
            return {
              id: item.Id,
              languageId: item.LanguageId,
              localeKey: item.LocaleKey,
              localeValue: item.LocaleValue,
            };
          }) ?? [],
      });
    } else
      setInitialValuesForm({
        name: '',
        code: '',
        shippingCost: 0,
        published: false,
        displayOrder: 0,
        regionId: '',
        locales: [],
      });
  }, [districtDetails]);

  const langComponent = [
    {
      code: LanguageCode.English,
      langComponent: (
        <ValueModalLanguage code={LanguageCode.English} index={0} />
      ),
    },
    {
      code: LanguageCode.Español,
      langComponent: (
        <ValueModalLanguage code={LanguageCode.Español} index={1} />
      ),
    },
    {
      code: LanguageCode.Arabic,
      langComponent: (
        <ValueModalLanguage code={LanguageCode.Arabic} index={2} />
      ),
    },
  ];
  const handleSubmit = async (values: any, setSubmitting: any) => {
    try {
      if (!districtDetails) {
        await countriesService.districtCreatePopup({
          ...values,
          name: !MainUtils.isEmptyValue(
            getLocaleValue(values?.locales, LanguageCode.English, 'name'),
          )
            ? getLocaleValue(values?.locales, LanguageCode.English, 'name')
            : getLocaleValue(values?.locales, LanguageCode.Arabic, 'name'),
          regionId: regionId,
        });
      } else {
        await countriesService.districtEditPopup({
          ...values,
          name: !MainUtils.isEmptyValue(
            getLocaleValue(values?.locales, LanguageCode.English, 'name'),
          )
            ? getLocaleValue(values?.locales, LanguageCode.English, 'name')
            : getLocaleValue(values?.locales, LanguageCode.Arabic, 'name'),
          regionId: regionId,
        });
      }
      onFinish();
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValuesForm}
      validationSchema={CreateOrUpdateDistrictSchema}
      onSubmit={(values, { setSubmitting }: any) =>
        handleSubmit(values, setSubmitting)
      }
      validateOnChange={false}
      validateOnBlur={false}>
      {({ handleSubmit, isSubmitting, setFieldValue }: any) => (
        <Form>
          <Modal
            width={'700px'}
            onCancel={() => onCancel()}
            centered
            open={visible}
            onOk={handleSubmit}
            okButtonProps={{ loading: isSubmitting }}
            title={
              <>
                <div className='text-title'>
                  <span className='icon-title'>
                    <AiFillEnvironment />
                  </span>
                  {messages[
                    'countries.region.districts.addNewDistrict'
                  ].toString()}
                </div>
              </>
            }>
            <Row gutter={[0, 20]}>
              <Col span={24}>
                <div className='languages-tab'>
                  <LanguagesTab
                    languagesList={langComponent}
                    tabItem={{
                      key: 'Standard',
                      label: `${messages[
                        'category.categoryInfo.Standard'
                      ].toString()}`,
                      children: (
                        <ValueModalLanguage
                          code={`${!MainUtils.isEmptyValue(locale.code)
                            ? locale.code
                            : LanguageCode.English
                            }`}
                        />
                      ),
                    }}
                  />
                </div>
              </Col>

              <Col span={24}>
                <FormItem
                  name='code'
                  label={messages['manageShops.code'].toString()}
                  fieldCommponent={<Input name='code' />}
                />
              </Col>

              <Col span={24}>
                <FormItem
                  name='shippingCost'
                  label={messages['orders.shippingCost'].toString()}
                  fieldCommponent={
                    <Input
                      name='shippingCost'
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validationInputNumber(value)) {
                          setFieldValue('shippingCost', Number(value));
                        } else setFieldValue('shippingCost', 0);
                      }}
                    />
                  }
                />
              </Col>

              <Col span={24}>
                <FormItem
                  name={'published'}
                  label={messages['countries.Published'].toString()}
                  fieldCommponent={
                    <Switch
                      name={'published'}
                      checkedChildren={<IntlMessages id='common.active' />}
                      unCheckedChildren={<IntlMessages id='common.inactive' />}
                    />
                  }
                />
              </Col>

              <Col span={24}>
                <FormItem
                  name='displayOrder'
                  label={messages[
                    'productAttributes.productAttributesPredefinedValues.displayOrder'
                  ].toString()}
                  fieldCommponent={
                    <Input
                      name='displayOrder'
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validationInputNumber(value)) {
                          setFieldValue('displayOrder', Number(value));
                        } else setFieldValue('displayOrder', 0);
                      }}
                    />
                  }
                />
              </Col>
            </Row>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default CreateOrUpdateDistrictModal;
